import React from 'react'
import { Box } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { RecommendationsContainer } from '../../../interaxDesignSystemComponents/RecommendationsContainer'
import { SectionHeader } from './cards'
import { formatFeedbackText } from '../../feedbackGenerator/helpers/textFormatter'
import type { DraftJsContent } from 'pure-js/libs/Common'
import { draftJsContentToHtml, isDraftJsContent } from '../../feedbackGenerator/helpers/draftJsToHtml'

export interface TryThisNextTimeComponentProps {
  tryThisNextTime?: string | DraftJsContent
  className?: string
}

/**
 * TryThisNextTimeComponent - Displays formatted "Try This Next Time" feedback text
 * Shows formatted feedback text from latest session feedback with markdown-like formatting support
 */
export const TryThisNextTimeComponent: React.FC<TryThisNextTimeComponentProps> = ({
  tryThisNextTime,
  className = ''
}) => {
  const { t } = useTranslation()

  // Format the text using formatFeedbackText helper
  const formattedText = React.useMemo(() => {
    if (!tryThisNextTime) {
      return ''
    }
    if (isDraftJsContent(tryThisNextTime)) {
      return draftJsContentToHtml(tryThisNextTime)
    }
    if (typeof tryThisNextTime === 'string') {
      return formatFeedbackText(tryThisNextTime)
    }
    return ''
  }, [tryThisNextTime])

  return (
    <RecommendationsContainer className={className}>
      {/* Header */}
      <SectionHeader
        title={t('gamer.dashboard.tryThisNextTime.title', 'Try This Next Time')}
        subtitle={t(
          'gamer.dashboard.tryThisNextTime.subtitle',
          'Your top recommendation for improvement in your next game session.'
        )}
      />

      {/* Formatted Text Content in Glass Box */}
      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '24px',
          width: '100%',
          background: `linear-gradient(144.63deg, rgba(46, 79, 95, 0.15) 0%, rgba(46, 79, 95, 0.08) 100%), ${INTERAX_COLORS.heritageBlue40}`,
          border: `1px solid rgba(46, 79, 95, 0.2)`,
          boxShadow: `0px 12px 24px rgba(0, 0, 0, 0.25), inset 0px 1px 0px rgba(46, 79, 95, 0.2)`,
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          position: 'relative',
          minHeight: '120px',
          maxHeight: '500px',
          overflowX: 'hidden',
          overflowY: 'auto',
          // Hide scrollbar but keep scrolling functional
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE and Edge
          '&::-webkit-scrollbar': {
            display: 'none' // Chrome, Safari, Opera
          }
        }}
      >
        {formattedText ? (
          <Box
            sx={{
              ...INTERAX_TYPOGRAPHY.body3,
              color: INTERAX_COLORS.typographyFont,
              lineHeight: 1.6,
              width: '100%',
              '& p': {
                marginBottom: '8px',
                '&:last-child': {
                  marginBottom: 0
                }
              },
              '& ol, & ul': {
                marginLeft: '20px',
                marginBottom: '8px',
                '& li': {
                  marginBottom: '4px'
                }
              },
              '& strong': {
                fontWeight: 600
              }
            }}
            dangerouslySetInnerHTML={{ __html: formattedText }}
          />
        ) : (
          <Box
            sx={{
              ...INTERAX_TYPOGRAPHY.body3,
              color: INTERAX_COLORS.typographyFont,
              width: '100%'
            }}
          >
            {t('gamer.dashboard.tryThisNextTime.empty', 'No feedback available yet.')}
          </Box>
        )}
      </Box>
    </RecommendationsContainer>
  )
}

export default TryThisNextTimeComponent
